// File: e1c/src/components/content_editor/QuizChoiceBlock.js
// Description: A new, dedicated block for creating quiz questions (single/multi-choice)
// with media, correct answer marking, and feedback.
// MODIFIED: Added a top-level "Overall Question Feedback" field.

import React, { useCallback, useMemo } from 'react';
import {
    Paper, TextField, IconButton, Box, Grid, Typography, Button,
    RadioGroup, FormControlLabel, Radio, Checkbox, Tooltip, FormGroup,
    FormControl, FormHelperText, Divider, Switch
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import MediaAttachmentManager from './MediaAttachmentManager'; 

// --- Local helper to generate option values ---
const generateValueForChoiceOption = () =>
  `val_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

const generateInternalOptionId = () => `temp_opt_id_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

const QuizChoiceBlock = React.memo(({ id, question, options = {}, onChange, onDelete }) => {

    // --- UPDATED: Memoize all options, including new quiz fields ---
    const memoizedOptions = useMemo(() => ({
        choices: Array.isArray(options.choices) ? options.choices : [],
        isMultiSelect: !!options.isMultiSelect,
        media_assets: options.media_assets || [],
        question_feedback: options.question_feedback || '' // <-- ADD THIS LINE
    }), [options]);

    // Derive internal state from props for stable rendering
    const internalChoices = useMemo(() => {
        return memoizedOptions.choices.map(c => ({
            id: String(c.value || c.id || generateInternalOptionId()), // Ensure a stable key
            label: String(c.label || ''),
            value: String(c.value || c.id), // Ensure value is set
            is_correct: !!c.is_correct,
            feedback: String(c.feedback || '') // This is now for PER-CHOICE feedback
        }));
    }, [memoizedOptions.choices]);

    const isMultiSelect = memoizedOptions.isMultiSelect;

    const handleQuestionChange = useCallback((event) => {
        if (onChange) {
            onChange({ question: event.target.value });
        }
    }, [onChange]);

    // --- NEW: Handle generic option changes (for feedback) ---
    const handleGenericOptionChange = useCallback((optionName, value) => {
        if (onChange) {
            onChange({ options: { [optionName]: value } });
        }
    }, [onChange]);

    // --- NEW: Handle media changes ---
    const handleMediaChange = useCallback((newMedia) => {
        if (onChange) {
            onChange({ options: { media_assets: newMedia.media_assets } });
        }
    }, [onChange]);
    
    // --- NEW: Handle Multi-Select toggle ---
    const handleMultiSelectToggle = useCallback((event) => {
        const newIsMultiSelect = event.target.checked;
        const updatedChoices = internalChoices.map(choice => ({
            ...choice,
            // When switching to single-select, ensure only one (or zero) is correct
            is_correct: newIsMultiSelect ? choice.is_correct : false 
        }));
        
        if (onChange) {
            onChange({
                options: { isMultiSelect: newIsMultiSelect },
                internalChoices: updatedChoices // Send updated choices to clear selections
            });
        }
    }, [onChange, internalChoices]);


    // Central function to propagate choice array changes to the parent
    const updateChoices = useCallback((newChoices) => {
        if (onChange) {
            // The parent (VisualContentEditor) handles this `internalChoices` key
            onChange({ internalChoices: newChoices });
        }
    }, [onChange]);

    // --- UPDATED: Handle changes to any field in an option ---
    const handleOptionChange = useCallback((optionIdToUpdate, field, newValue) => {
        const updatedChoices = internalChoices.map(opt => {
            if (opt.id === optionIdToUpdate) {
                return { ...opt, [field]: newValue };
            }
            return opt;
        });
        updateChoices(updatedChoices);
    }, [internalChoices, updateChoices]);

    // --- UPDATED: Handle marking an answer as correct ---
    const handleMarkCorrect = useCallback((optionIdToToggle) => {
        let updatedChoices;
        if (isMultiSelect) {
            // Checkbox behavior: Toggle the 'is_correct' status
            updatedChoices = internalChoices.map(opt => 
                opt.id === optionIdToToggle ? { ...opt, is_correct: !opt.is_correct } : opt
            );
        } else {
            // Radio button behavior: Set this one true, all others false
            updatedChoices = internalChoices.map(opt => ({
                ...opt,
                is_correct: opt.id === optionIdToToggle
            }));
        }
        updateChoices(updatedChoices);
    }, [internalChoices, isMultiSelect, updateChoices]);

    const handleAddOption = useCallback(() => {
        const newOption = { id: generateInternalOptionId(), label: '', value: generateValueForChoiceOption(), is_correct: false, feedback: '' };
        updateChoices([...internalChoices, newOption]);
    }, [internalChoices, updateChoices]);

    const handleRemoveOption = useCallback((optionIdToRemove) => {
        let updatedChoices = internalChoices.filter(opt => opt.id !== optionIdToRemove);
        if (updatedChoices.length === 0) {
            updatedChoices = [{ id: generateInternalOptionId(), label: '', value: generateValueForChoiceOption(), is_correct: false, feedback: '' }];
        }
        updateChoices(updatedChoices);
    }, [internalChoices, updateChoices]);

    const handleDeleteClick = useCallback(() => {
        if (onDelete) { onDelete(id); }
    }, [id, onDelete]);

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, position: 'relative', backgroundColor: 'grey.50', '&:hover .delete-button': { opacity: 1 } }}>
            <IconButton
                className="delete-button" aria-label="delete block" onClick={handleDeleteClick} size="small"
                sx={{ position: 'absolute', top: 8, right: 8, color: 'grey.500', opacity: 0.5, transition: 'opacity 0.2s', '&:hover': { opacity: 1, color: 'error.main' } }}
            >
                <DeleteOutlineIcon fontSize="small"/>
            </IconButton>

            <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 'bold' }}>
                Quiz Question (Choice)
            </Typography>

            <TextField
                fullWidth variant="outlined" size="small"
                label={`Quiz Question Text (ID: ${id})`}
                multiline
                value={question || ''}
                onChange={handleQuestionChange}
                placeholder="Enter the quiz question..." sx={{ mb: 2 }}
            />
            
            {/* --- NEW: MEDIA MANAGER --- */}
            <MediaAttachmentManager
                mediaAssets={memoizedOptions.media_assets}
                onChange={handleMediaChange}
            />

            <FormControlLabel
                control={<Switch checked={isMultiSelect} onChange={handleMultiSelectToggle} />}
                label="Allow multiple correct answers"
                sx={{ mt: 1, mb: 1 }}
            />
            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
                Options & Per-Choice Feedback:
            </Typography>

             {internalChoices.map((option, index) => (
                <Paper variant="outlined" sx={{ p: 2, mb: 1.5, position: 'relative' }} key={option.id}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={1}>
                            <Tooltip title={isMultiSelect ? "Toggle correct answer" : "Set as correct answer"}>
                                <IconButton onClick={() => handleMarkCorrect(option.id)} color={option.is_correct ? "success" : "default"}>
                                    {option.is_correct ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item xs={10} sm={7}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                label={`Option ${index + 1} Label`}
                                value={option.label}
                                onChange={(e) => handleOptionChange(option.id, 'label', e.target.value)}
                                placeholder="Enter option label"
                            />
                        </Grid>

                        {/* --- Remove Button --- */}
                        <Grid item xs={1}>
                            <Tooltip title="Remove this option">
                                <span>
                                    <IconButton
                                        onClick={() => handleRemoveOption(option.id)}
                                        size="small"
                                        disabled={internalChoices.length <= 1}
                                    >
                                        <RemoveCircleOutlineIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Grid>

                        {/* --- Feedback Field --- */}
                        <Grid item xs={12} sm_offset={1} sm={11}>
                             <TextField
                                fullWidth
                                variant="filled"
                                size="small"
                                label="Feedback / Explanation for this specific option (Optional)"
                                value={option.feedback}
                                onChange={(e) => handleOptionChange(option.id, 'feedback', e.target.value)}
                                placeholder="e.g., 'Correct! The RICE method is best...'"
                                sx={{ 
                                    mt: 1, 
                                    backgroundColor: option.is_correct ? 'success.lightest' : 'grey.100'
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            ))}
            
            <Button startIcon={<AddCircleOutlineIcon />} onClick={handleAddOption} size="small" sx={{ mt: 1 }}>
                Add Option
            </Button>
            
            {/* --- START OF ADDITION --- */}
            <Divider sx={{ my: 2 }} />
            <TextField
                fullWidth
                variant="filled"
                size="small"
                label="Overall Question Feedback / Explanation"
                multiline
                minRows={2}
                value={memoizedOptions.question_feedback}
                onChange={(e) => handleGenericOptionChange('question_feedback', e.target.value)}
                placeholder="Enter the general explanation for this question (e.g., 'Explanation: That's right! Protein is the main...')"
                sx={{ mt: 1, backgroundColor: 'success.lightest' }}
                helperText="This feedback will be shown after the patient answers, regardless of which option they pick."
            />
            {/* --- END OF ADDITION --- */}

        </Paper>
    );
});

export default QuizChoiceBlock;